import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth, adminApi, formatApiError } from "./AuthContext";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user === null) return null; // still loading
  if (user === false) return <Navigate to="/admin/login" replace />;

  const forced = Boolean(user.must_change_password);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 10) {
      setErr("New password must be at least 10 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    if (next === current) {
      setErr("New password must be different from your current password.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      // Persist new auth — server already set a fresh cookie and returned a token.
      if (data?.access_token) {
        localStorage.setItem("tns_admin_token", data.access_token);
      }
      if (data?.user) setUser(data.user);
      navigate("/admin", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="change-password-page"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#0A0F1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
        padding: 24,
      }}
    >
      <form
        onSubmit={submit}
        data-testid="change-password-form"
        style={{
          width: 380,
          padding: 28,
          background: "#101C35",
          border: "1px solid #14213D",
        }}
      >
        <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: 3 }}>
          THE NEXT STAGE
        </div>
        <h1
          style={{
            margin: "8px 0 6px 0",
            fontSize: 22,
            letterSpacing: 0.5,
          }}
        >
          {forced ? "Set a new password to continue" : "Change your password"}
        </h1>
        <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 18 }}>
          {forced
            ? "An administrator gave you a temporary password. Choose a new one before continuing."
            : `Signed in as ${user.email}.`}
        </p>

        <Label>Current password</Label>
        <input
          data-testid="change-password-current"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          style={inputStyle}
        />

        <Label>New password (min 10 characters)</Label>
        <input
          data-testid="change-password-new"
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          style={inputStyle}
        />

        <Label>Confirm new password</Label>
        <input
          data-testid="change-password-confirm"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          style={inputStyle}
        />

        {err && (
          <div
            data-testid="change-password-error"
            style={{ color: "#ff6b6b", marginTop: 12, fontSize: 13 }}
          >
            {err}
          </div>
        )}

        <button
          data-testid="change-password-submit"
          type="submit"
          disabled={busy}
          style={{
            marginTop: 18,
            width: "100%",
            padding: "10px 14px",
            background: "#E5B25D",
            color: "#0A0F1F",
            border: 0,
            fontWeight: 700,
            letterSpacing: 1,
            cursor: busy ? "wait" : "pointer",
          }}
        >
          {busy ? "Saving…" : "UPDATE PASSWORD"}
        </button>

        {!forced && (
          <button
            type="button"
            data-testid="change-password-cancel"
            onClick={() => navigate("/admin")}
            style={{
              marginTop: 10,
              width: "100%",
              padding: "8px 14px",
              background: "transparent",
              color: "#fff",
              border: "1px solid #2a3556",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Cancel
          </button>
        )}
        {forced && (
          <button
            type="button"
            data-testid="change-password-logout"
            onClick={logout}
            style={{
              marginTop: 10,
              width: "100%",
              padding: "8px 14px",
              background: "transparent",
              color: "#aab1c2",
              border: "1px solid #2a3556",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Sign out instead
          </button>
        )}
      </form>
    </div>
  );
}

function Label({ children }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: 11,
        letterSpacing: 1,
        opacity: 0.7,
        marginTop: 14,
      }}
    >
      {children.toString().toUpperCase()}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  background: "#0A0F1F",
  border: "1px solid #2a3556",
  color: "#fff",
  borderRadius: 2,
  marginTop: 4,
  fontSize: 14,
  boxSizing: "border-box",
};
